/**
 * Vendored from AudioCN (https://github.com/audiocn/ui), hand-copied at
 * commit 42f863ec333c6912c421d1e9d6d7ce7850ce2680.
 * Source file: components/ui/waveform.tsx
 * License: MIT, Copyright (c) 2026 OrcDev. See components/ui/audiocn/LICENSE.md.
 *
 * Presentation only. It takes peaks, a duration and a playhead as props and
 * never loads, fetches or decodes audio. Its data and playback helpers
 * (useWaveformData, loadAudioBuffer, the audio player hook) are not vendored
 * and are fenced out by no-restricted-imports in eslint.config.mjs.
 *
 * Local changes (Plate & Hairline restyle, then behaviour patches):
 *   Restyle
 *   - Square corners (--ls-radius-0), --tls-* tokens, no shadcn tokens, no
 *     rounded bars: barRadius and the roundRect path are gone.
 *   - No pulsing skeleton. A loading waveform draws nothing.
 *   - No hover line or hover time: WaveformHover and its hover channel are
 *     removed. WaveformMarker is removed (it used the amber meter token).
 *   - Handles keep a 44px hit area (w-11) around an 8px accent bar, and use
 *     the repo focus ring (tlsr-focus-inset) instead of ring-3.
 *   - No arbitrary px or rem values, so check:design-drift does not rise.
 *   Behaviour
 *   - Waveform: step defaults to 0.1 s and largeStep to 0.5 s (was 5 s and
 *     15 s). aria-valuenow and aria-valuetext read tenths of a second, not
 *     whole seconds, so a 1.4 s sound does not read "0:01 of 0:01".
 *   - Waveform: touch-none applies only while the waveform is interactive, so
 *     a display-only waveform does not trap a page scroll.
 *   - WaveformRegion: new maxLength (longest window, enforced while dragging),
 *     onValueCommit (fires once when a drag ends, not on every move),
 *     disabled, and startHandleProps / endHandleProps to override a handle's
 *     label, aria values and key handling.
 *   - WaveformRegion: the region body pans vertically so a swipe on a phone
 *     still scrolls the page. Horizontal drags move the region.
 *   - Waveform: a touch or pen press scrubs only after 8 px of sideways travel
 *     or on release, so a press that becomes a scroll never moves the
 *     playhead. pointercancel and lostpointercapture end the drag without
 *     committing, a move with no button down ends it too, and each of those
 *     fires a new onSeekCancel and puts the playhead back. Consumer pointer
 *     handlers are chained rather than replacing the seek handlers.
 */

"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ComponentProps,
  CSSProperties,
  KeyboardEvent,
  PointerEvent,
} from "react";

import { clamp } from "@/lib/audiocn/clamp";
import { resampleLevels } from "@/lib/audiocn/resample-levels";
import { subscribeFrame } from "@/lib/audiocn/frame-loop";
import { formatTime } from "@/lib/audiocn/time";
import type { FrameSource } from "@/lib/audiocn/types";
import { useFrameSource } from "@/lib/audiocn/use-frame-source";
import { useVisibility } from "@/lib/audiocn/use-visibility";
import { cn } from "@/lib/utils";

const COLOR_REFRESH_FRAMES = 30;

interface WaveformContextValue {
  peaks: ArrayLike<number> | null;
  duration: number;
  variant: "bars" | "line" | "mirror";
  barWidth: number;
  barGap: number;
  loading: boolean;
  /** Current progress, 0..1, read by the canvas and cursor on every frame. */
  progressRef: { current: number };
  timeToPosition: (time: number) => number;
}

const WaveformContext = createContext<WaveformContextValue | null>(null);

const useWaveform = (part: string) => {
  const context = useContext(WaveformContext);
  if (!context) {
    throw new Error(`${part} must be used inside Waveform.`);
  }
  return context;
};

const seekTarget = (
  key: string,
  current: number,
  amount: number,
  duration: number
): number | null => {
  const targets: Record<string, number> = {
    ArrowDown: current - amount,
    ArrowLeft: current - amount,
    ArrowRight: current + amount,
    ArrowUp: current + amount,
    End: duration,
    Home: 0,
  };
  return targets[key] ?? null;
};

const toTenths = (value: number) => Math.round(value * 10) / 10;

export const WaveformCanvas = ({
  className,
  ...props
}: ComponentProps<"canvas">) => {
  const { barGap, barWidth, loading, peaks, progressRef, variant } =
    useWaveform("WaveformCanvas");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const visibleRef = useVisibility(canvasRef);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!(canvas && context)) {
      return;
    }
    const size = { height: 0, width: 0 };
    let ratio = 1;
    let colors = { played: "", unplayed: "" };
    let framesSinceColor = COLOR_REFRESH_FRAMES;
    let lastProgress = -1;
    let levels = new Float32Array(0);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      ratio = window.devicePixelRatio || 1;
      size.width = rect.width;
      size.height = rect.height;
      canvas.width = Math.max(1, Math.round(size.width * ratio));
      canvas.height = Math.max(1, Math.round(size.height * ratio));
      const count = Math.max(
        1,
        Math.floor((size.width + barGap) / (barWidth + barGap))
      );
      levels = new Float32Array(count);
      if (peaks && peaks.length > 0) {
        resampleLevels(peaks, 0, peaks.length, levels);
      }
      lastProgress = -1;
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const drawPass = (color: string, clipWidth: number) => {
      context.save();
      context.beginPath();
      context.rect(0, 0, clipWidth, size.height);
      context.clip();
      context.fillStyle = color;
      context.strokeStyle = color;
      const pitch = barWidth + barGap;
      const middle = size.height / 2;
      if (variant === "line") {
        context.lineWidth = 1.5;
        context.beginPath();
        for (let index = 0; index < levels.length; index += 1) {
          const x = index * pitch + barWidth / 2;
          const y = middle - (levels[index] ?? 0) * (middle - 1);
          if (index === 0) {
            context.moveTo(x, y);
          } else {
            context.lineTo(x, y);
          }
        }
        for (let index = levels.length - 1; index >= 0; index -= 1) {
          const x = index * pitch + barWidth / 2;
          context.lineTo(x, middle + (levels[index] ?? 0) * (middle - 1));
        }
        context.closePath();
        context.fill();
      } else {
        for (let index = 0; index < levels.length; index += 1) {
          const level = levels[index] ?? 0;
          const barHeight = Math.max(2, level * size.height);
          const y =
            variant === "mirror"
              ? middle - barHeight / 2
              : size.height - barHeight;
          context.fillRect(index * pitch, y, barWidth, barHeight);
        }
      }
      context.restore();
    };

    const draw = () => {
      framesSinceColor += 1;
      if (framesSinceColor >= COLOR_REFRESH_FRAMES) {
        framesSinceColor = 0;
        const style = getComputedStyle(canvas);
        const next = {
          played:
            style.getPropertyValue("--waveform-progress").trim() || style.color,
          unplayed: style.getPropertyValue("--waveform").trim() || style.color,
        };
        if (
          next.played !== colors.played ||
          next.unplayed !== colors.unplayed
        ) {
          colors = next;
          lastProgress = -1;
        }
      }
      const progress = progressRef.current;
      // Off screen, leave lastProgress alone so it repaints when it returns.
      if (
        progress === lastProgress ||
        size.width === 0 ||
        !visibleRef.current
      ) {
        return;
      }
      lastProgress = progress;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, size.width, size.height);
      if (loading || !peaks) {
        return;
      }
      drawPass(colors.unplayed, size.width);
      drawPass(colors.played, progress * size.width);
    };

    const unsubscribe = subscribeFrame(draw);
    return () => {
      unsubscribe();
      observer.disconnect();
    };
  }, [barGap, barWidth, loading, peaks, progressRef, variant, visibleRef]);

  return (
    <canvas
      aria-hidden
      className={cn("absolute inset-0 size-full", className)}
      data-slot="waveform-canvas"
      ref={canvasRef}
      {...props}
    />
  );
};

export const WaveformCursor = ({
  className,
  style,
  ...props
}: ComponentProps<"div">) => (
  <div
    aria-hidden
    className={cn(
      "pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-(--waveform-cursor)",
      className
    )}
    data-slot="waveform-cursor"
    style={{ left: "calc(var(--waveform-position) * 100%)", ...style }}
    {...props}
  />
);

export interface WaveformRegionValue {
  start: number;
  end: number;
}

export interface WaveformRegionProps extends Omit<
  ComponentProps<"div">,
  "onChange" | "defaultValue"
> {
  start: number;
  end: number;
  /** Fires on every move of a drag or a key press. */
  onValueChange?: (value: WaveformRegionValue) => void;
  /** Fires once when a drag ends, with the last value it reported. */
  onValueCommit?: (value: WaveformRegionValue) => void;
  /** Drag the edges. Default true. */
  resizable?: boolean;
  /** Drag the whole region. Default true. */
  draggable?: boolean;
  /** The region shows but does not move. Default false. */
  disabled?: boolean;
  /** Shortest region in seconds. Default 0.1. */
  minLength?: number;
  /** Longest region in seconds, enforced while dragging. Default no limit. */
  maxLength?: number;
  /** Overrides for the start handle: label, aria values, key handling. */
  startHandleProps?: ComponentProps<"span">;
  /** Overrides for the end handle. */
  endHandleProps?: ComponentProps<"span">;
}

interface RegionDrag {
  kind: "start" | "end" | "move";
  x: number;
  start: number;
  end: number;
}

const handleClass =
  "tlsr-focus-inset absolute inset-y-0 z-10 w-11 cursor-ew-resize touch-none outline-none before:absolute before:-z-10 before:inset-y-0 before:left-1/2 before:w-2 before:-translate-x-1/2 before:bg-[var(--tls-brand)]/70 focus-visible:before:bg-[var(--tls-brand)] aria-disabled:cursor-default";

export const WaveformRegion = ({
  start,
  end,
  onValueChange,
  onValueCommit,
  resizable = true,
  draggable = true,
  disabled = false,
  minLength = 0.1,
  maxLength = Number.POSITIVE_INFINITY,
  startHandleProps,
  endHandleProps,
  className,
  children,
  style,
  ...props
}: WaveformRegionProps) => {
  const { duration, timeToPosition } = useWaveform("WaveformRegion");
  const dragRef = useRef<RegionDrag | null>(null);
  const lastValueRef = useRef<WaveformRegionValue | null>(null);
  const regionRef = useRef<HTMLDivElement>(null);

  const secondsPerPixel = () => {
    const width =
      regionRef.current?.parentElement?.getBoundingClientRect().width ?? 1;
    return duration / width;
  };

  const report = (value: WaveformRegionValue) => {
    lastValueRef.current = value;
    onValueChange?.(value);
  };

  const begin =
    (kind: RegionDrag["kind"]) => (event: PointerEvent<HTMLElement>) => {
      event.stopPropagation();
      if (disabled) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      lastValueRef.current = null;
      dragRef.current = { end, kind, start, x: event.clientX };
    };

  const move = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    event.stopPropagation();
    const delta = (event.clientX - drag.x) * secondsPerPixel();
    if (drag.kind === "move") {
      const length = drag.end - drag.start;
      const nextStart = clamp(drag.start + delta, 0, duration - length);
      report({ end: nextStart + length, start: nextStart });
    } else if (drag.kind === "start") {
      report({
        end: drag.end,
        start: clamp(
          drag.start + delta,
          Math.max(0, drag.end - maxLength),
          drag.end - minLength
        ),
      });
    } else {
      report({
        end: clamp(
          drag.end + delta,
          drag.start + minLength,
          Math.min(duration, drag.start + maxLength)
        ),
        start: drag.start,
      });
    }
  };

  const finish = (event: PointerEvent<HTMLElement>) => {
    event.stopPropagation();
    const finished = dragRef.current;
    dragRef.current = null;
    if (finished && lastValueRef.current) {
      onValueCommit?.(lastValueRef.current);
    }
    lastValueRef.current = null;
  };

  const nudge =
    (edge: "start" | "end") => (event: KeyboardEvent<HTMLSpanElement>) => {
      if (disabled) {
        return;
      }
      const amount = event.shiftKey ? 1 : 0.1;
      let delta = 0;
      if (event.key === "ArrowRight" || event.key === "ArrowUp") {
        delta = amount;
      } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
        delta = -amount;
      }
      if (delta === 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (edge === "start") {
        onValueChange?.({
          end,
          start: clamp(
            start + delta,
            Math.max(0, end - maxLength),
            end - minLength
          ),
        });
      } else {
        onValueChange?.({
          end: clamp(
            end + delta,
            start + minLength,
            Math.min(duration, start + maxLength)
          ),
          start,
        });
      }
    };

  const left = timeToPosition(start) * 100;
  const width = (timeToPosition(end) - timeToPosition(start)) * 100;
  const canMove = draggable && !disabled;

  return (
    <div
      className={cn(
        "absolute inset-y-0 left-(--region-start) w-(--region-size) rounded-[var(--ls-radius-0)] bg-[var(--tls-brand)]/20",
        canMove && "cursor-grab touch-pan-y active:cursor-grabbing",
        className
      )}
      data-disabled={disabled ? "" : undefined}
      data-slot="waveform-region"
      onLostPointerCapture={finish}
      onPointerDown={
        canMove ? begin("move") : (event) => event.stopPropagation()
      }
      onPointerMove={move}
      onPointerUp={finish}
      ref={regionRef}
      style={
        {
          "--region-size": `${width}%`,
          "--region-start": `${left}%`,
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      {children}
      {resizable ? (
        <>
          <span
            aria-disabled={disabled || undefined}
            aria-label="Region start"
            aria-valuemax={toTenths(end)}
            aria-valuemin={0}
            aria-valuenow={toTenths(start)}
            aria-valuetext={formatTime(start)}
            className={cn(handleClass, "left-0 -translate-x-1/2")}
            data-slot="waveform-region-start"
            onKeyDown={nudge("start")}
            onPointerDown={begin("start")}
            role="slider"
            tabIndex={0}
            {...startHandleProps}
          />
          <span
            aria-disabled={disabled || undefined}
            aria-label="Region end"
            aria-valuemax={toTenths(duration)}
            aria-valuemin={toTenths(start)}
            aria-valuenow={toTenths(end)}
            aria-valuetext={formatTime(end)}
            className={cn(handleClass, "right-0 translate-x-1/2")}
            data-slot="waveform-region-end"
            onKeyDown={nudge("end")}
            onPointerDown={begin("end")}
            role="slider"
            tabIndex={0}
            {...endHandleProps}
          />
        </>
      ) : null}
    </div>
  );
};

export interface WaveformProps extends Omit<
  ComponentProps<"div">,
  "onSeeked" | "defaultValue"
> {
  /** Peaks, 0..1, from computePeaks. */
  peaks: ArrayLike<number> | null;
  /** Length in seconds. */
  duration: number;
  /** Playhead position in seconds. */
  currentTime?: number;
  defaultCurrentTime?: number;
  /** A smooth playhead with no React renders. */
  time?: FrameSource<number> | null;
  /** Fires while seeking. */
  onSeek?: (time: number) => void;
  /** Fires when the pointer is released or after keyboard seeking. */
  onSeekCommitted?: (time: number) => void;
  /**
   * Fires when a drag ends without a seek: pointercancel, lost capture, or a
   * move with no button down. Not on pointerup, which commits instead.
   */
  onSeekCancel?: () => void;
  /** Seconds per arrow key. Default 0.1. */
  step?: number;
  /** Seconds per Shift+arrow. Default 0.5. */
  largeStep?: number;
  /** Default `bars`. */
  variant?: "bars" | "line" | "mirror";
  /** Bar width in pixels. Default 2. */
  barWidth?: number;
  /** Gap in pixels. Default 1. */
  barGap?: number;
  /** When false, the waveform is display only. Default true. */
  interactive?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

const isInteractive = (
  interactive: boolean,
  disabled: boolean,
  loading: boolean,
  duration: number
) => interactive && !disabled && !loading && duration > 0;

interface PointerOptions {
  seekTo: (time: number, commit: boolean) => void;
  timeAtPointer: (event: PointerEvent<HTMLDivElement>) => number;
  /** A drag ended without committing. */
  onCancel: () => void;
}

/** Horizontal travel before a touch or pen press starts scrubbing (§03.3). */
const TOUCH_SCRUB_SLOP = 8;

/**
 * Pointer seeking for the waveform root.
 *
 * A mouse press seeks at once. A touch or pen press waits until it has moved
 * TOUCH_SCRUB_SLOP pixels sideways, or lifts as a tap, so a press that turns
 * into a page scroll never moves the playhead. A cancelled press or a lost
 * capture ends the drag without committing.
 */
const useWaveformPointer = ({ seekTo, timeAtPointer, onCancel }: PointerOptions) => {
  const [dragging, setDragging] = useState(false);
  const pressRef = useRef<{ x: number; scrubbing: boolean } | null>(null);

  const end = () => {
    pressRef.current = null;
    setDragging(false);
  };

  const cancel = () => {
    if (!pressRef.current) {
      return;
    }
    end();
    onCancel();
  };

  return {
    dragging,
    handlers: {
      onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
          return;
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        const deferred = event.pointerType === "touch" || event.pointerType === "pen";
        pressRef.current = { x: event.clientX, scrubbing: !deferred };
        setDragging(true);
        if (!deferred) {
          seekTo(timeAtPointer(event), false);
        }
      },
      onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
        const press = pressRef.current;
        if (!press) {
          return;
        }
        if (event.buttons === 0) {
          cancel();
          return;
        }
        if (!press.scrubbing) {
          if (Math.abs(event.clientX - press.x) < TOUCH_SCRUB_SLOP) {
            return;
          }
          press.scrubbing = true;
        }
        seekTo(timeAtPointer(event), false);
      },
      onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
        if (!pressRef.current) {
          return;
        }
        end();
        seekTo(timeAtPointer(event), true);
      },
      onPointerCancel: cancel,
      onLostPointerCapture: cancel,
    },
  };
};

/** Calls ours, then the consumer's handler of the same name. */
const chain =
  <E,>(ours?: (event: E) => void, theirs?: (event: E) => void) =>
  (event: E) => {
    ours?.(event);
    theirs?.(event);
  };

export const Waveform = ({
  peaks,
  duration,
  currentTime,
  defaultCurrentTime = 0,
  time,
  onSeek,
  onSeekCommitted,
  onSeekCancel,
  step = 0.1,
  largeStep = 0.5,
  variant = "bars",
  barWidth = 2,
  barGap = 1,
  interactive = true,
  loading = false,
  disabled = false,
  className,
  children,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onLostPointerCapture,
  ...props
}: WaveformProps) => {
  const [internalTime, setInternalTime] = useState(defaultCurrentTime);
  const shownTime = currentTime ?? internalTime;
  const rootRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);
  const latestTimeRef = useRef(shownTime);

  const timeToPosition = useCallback(
    (value: number) => (duration > 0 ? clamp(value / duration, 0, 1) : 0),
    [duration]
  );

  const writeProgress = useCallback(
    (value: number) => {
      latestTimeRef.current = value;
      progressRef.current = timeToPosition(value);
      rootRef.current?.style.setProperty(
        "--waveform-position",
        progressRef.current.toFixed(5)
      );
    },
    [timeToPosition]
  );

  useEffect(() => {
    writeProgress(shownTime);
  }, [shownTime, writeProgress]);

  useFrameSource(time, writeProgress);

  const seekTo = (value: number, commit: boolean) => {
    const next = clamp(value, 0, duration);
    writeProgress(next);
    if (currentTime === undefined) {
      setInternalTime(next);
    }
    onSeek?.(next);
    if (commit) {
      onSeekCommitted?.(next);
    }
  };

  const timeAtPointer = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const position = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    return position * duration;
  };

  const active = isInteractive(interactive, disabled, loading, duration);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!active) {
      return;
    }
    const next = seekTarget(
      event.key,
      latestTimeRef.current,
      event.shiftKey ? largeStep : step,
      duration
    );
    if (next !== null) {
      event.preventDefault();
      seekTo(next, true);
    }
  };

  const contextValue = useMemo<WaveformContextValue>(
    () => ({
      barGap,
      barWidth,
      duration,
      loading,
      peaks,
      progressRef,
      timeToPosition,
      variant,
    }),
    [barGap, barWidth, duration, loading, peaks, timeToPosition, variant]
  );

  const pointer = useWaveformPointer({
    seekTo,
    timeAtPointer,
    // Put the playhead back where playback is, then let the consumer drop
    // any drag value it was holding.
    onCancel: () => {
      writeProgress(shownTime);
      onSeekCancel?.();
    },
  });

  const interactiveProps = active
    ? {
        "aria-valuemax": toTenths(duration),
        "aria-valuemin": 0,
        "aria-valuenow": toTenths(shownTime),
        "aria-valuetext": `${shownTime.toFixed(1)} s of ${duration.toFixed(1)} s`,
        onKeyDown: handleKeyDown,
        role: "slider",
        tabIndex: 0,
      }
    : {};
  const ours = active ? pointer.handlers : undefined;

  return (
    <WaveformContext.Provider value={contextValue}>
      <div
        className={cn(
          "group/waveform tlsr-focus-inset relative h-20 w-full rounded-[var(--ls-radius-0)] outline-none select-none [--waveform-cursor:var(--tls-fg-strong)] [--waveform-position:0] [--waveform-progress:var(--tls-brand)] [--waveform:var(--tls-fg-subtle)] data-disabled:opacity-50",
          active && "cursor-pointer touch-none",
          className
        )}
        data-disabled={disabled ? "" : undefined}
        data-dragging={pointer.dragging ? "" : undefined}
        data-loading={loading ? "" : undefined}
        data-slot="waveform"
        data-variant={variant}
        ref={rootRef}
        {...interactiveProps}
        {...props}
        onLostPointerCapture={chain(ours?.onLostPointerCapture, onLostPointerCapture)}
        onPointerCancel={chain(ours?.onPointerCancel, onPointerCancel)}
        onPointerDown={chain(ours?.onPointerDown, onPointerDown)}
        onPointerMove={chain(ours?.onPointerMove, onPointerMove)}
        onPointerUp={chain(ours?.onPointerUp, onPointerUp)}
      >
        {children ?? (
          <>
            <WaveformCanvas />
            <WaveformCursor />
          </>
        )}
      </div>
    </WaveformContext.Provider>
  );
};
